"use client"

import { useState, type FormEvent } from "react"
import { m, AnimatePresence } from "framer-motion"
import { expoOut } from "@/lib/motion"
import Image from "next/image"
import Link from "next/link"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { PhoneIcon } from "@/components/ui/icons"
import { useBusinessHours } from "@/components/chat/use-business-hours"
import { useChatLauncherContext } from "@/components/chat/chat-launcher-context"
import {
  getFilloutCallFormId,
  preconnectFillout,
  warmFillout,
} from "@/components/ui/fillout-embed"
import { BookCallModal } from "@/components/ui/book-call-modal"
import { track } from "@/lib/analytics"
import { useLocalePath, useLocalePrefix } from "@/lib/use-localized-navigation"
import { useCountry } from "@/components/layout/country-provider"
import { isAllowedInCountry } from "@/lib/country-gates"
import { contactIntentOptions } from "@/lib/contact-intent"
import { MARKET_SUPPORT } from "@/i18n/config"

import { cn } from "@/lib/utils"
import { sanitizeHtml } from "@/lib/sanitize"
import { contactKeys as k } from "@/i18n/contact-keys"
import { blobMediaUrl } from "@/lib/blob-url"

const PHONE_NUMBER_DISPLAY = "+41 44 797 69 46"
const PHONE_NUMBER_HREF = "tel:+41447976946"
const EMAIL_ADDRESS = "get@aheadhealth.com"

const TEAM_PHOTOS = [
  { src: blobMediaUrl("/images/team/nick.png"), alt: "Nick Lenten" },
  {
    src: blobMediaUrl("/images/team/ferdinand.png"),
    alt: "Ferdinand Skaugerum",
  },
  { src: blobMediaUrl("/images/team/dennis.png"), alt: "Dennis Hermann" },
  { src: blobMediaUrl("/images/avatars/laura.png"), alt: "Laura Leoty" },
]

/* ─────────────── Icons (page-local, not repeated elsewhere) ─────────────── */

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  )
}

function ChatBubbleIcon({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  )
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M21 11.5a8.4 8.4 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.4 8.4 0 01-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.4 8.4 0 013.8-.9h.5a8.5 8.5 0 018 8v.5z" />
    </svg>
  )
}

function EmailIcon({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 7l-10 7L2 7" />
    </svg>
  )
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M12 2l8 4v6c0 5-3.5 9.3-8 10-4.5-.7-8-5-8-10V6l8-4z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  )
}

function LiveDot() {
  return (
    <span className="relative flex h-2 w-2" aria-hidden="true">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
    </span>
  )
}

/* ───────────────────────── Intent cards ───────────────────────── */

type IntentCardProps = {
  icon: React.ReactNode
  title: string
  description: string
  action: string
  href?: string
  onClick?: () => void
  /** Called on hover / focus — use for just-in-time resource warmup. */
  onWarmup?: () => void
  trackLabel: string
  /** Show the live-now pulse next to the action (contact-during-hours). */
  liveIndicator?: boolean
}

function IntentCard({
  icon,
  title,
  description,
  action,
  href,
  onClick,
  onWarmup,
  trackLabel,
  liveIndicator,
}: IntentCardProps) {
  const content = (
    <div className="group relative flex h-full cursor-pointer flex-col rounded-[16px] bg-white p-6 text-neutral-900 transition-all duration-200 hover:-translate-y-[2px] hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-neutral-100 text-neutral-700">
        {icon}
      </div>
      <h3 className="type-headline-sm mt-5 text-neutral-900">{title}</h3>
      <p className="type-body-md mt-2 leading-relaxed text-neutral-500">
        {description}
      </p>
      <div className="text-aubergine-800 mt-auto flex items-center gap-2 pt-6 text-sm font-medium">
        {liveIndicator && <LiveDot />}
        <span>{action}</span>
        <ArrowRightIcon
          size={16}
          className="transition-transform duration-200 group-hover:translate-x-1"
        />
      </div>
    </div>
  )

  const handleClick = () => {
    track.ctaClick(trackLabel, "contact-intent")
    onClick?.()
  }

  const wrapperClasses = "block w-full h-full text-left"
  const warmupHandlers = onWarmup
    ? { onPointerEnter: onWarmup, onFocus: onWarmup }
    : undefined

  if (onClick && !href) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={wrapperClasses}
        {...warmupHandlers}
      >
        {content}
      </button>
    )
  }

  if (
    href?.startsWith("http") ||
    href?.startsWith("tel:") ||
    href?.startsWith("mailto:")
  ) {
    return (
      <a
        href={href}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
        onClick={handleClick}
        className={wrapperClasses}
        {...warmupHandlers}
      >
        {content}
      </a>
    )
  }

  return (
    <Link
      href={href ?? "#"}
      onClick={handleClick}
      className={wrapperClasses}
      {...warmupHandlers}
    >
      {content}
    </Link>
  )
}

/* ──────────────────────────── Page ──────────────────────────── */

interface ContactClientProps {
  initialFaqItems: {
    question: string
    answer: string
    slug?: string
    updatedAt?: string
    pages?: string[]
  }[]
}

export default function ContactClient({ initialFaqItems }: ContactClientProps) {
  const t = useTranslations("contact")
  const locale = useLocale()
  const lp = useLocalePath()
  const localePrefix = useLocalePrefix()
  const country = useCountry().code
  const {
    showBookCall,
    showPhone,
    showWhatsApp,
    showEmailCard,
    cardCount: intentCardCount,
    showIntentSection,
  } = contactIntentOptions(country)
  // Live-now dot during business hours. `null` on SSR/first paint (no dot),
  // then the real boolean once the client hook resolves.
  const liveNow = useBusinessHours()
  const { requestPanelOpen } = useChatLauncherContext()
  const intentGridCols =
    intentCardCount >= 4
      ? "md:grid-cols-2 lg:grid-cols-4"
      : intentCardCount === 3
        ? "md:grid-cols-3"
        : intentCardCount === 2
          ? "md:grid-cols-2"
          : ""
  // Native `required` / `type="email"` tooltips speak the visitor's BROWSER
  // language ("Please fill out this field" on a German page with an English
  // browser). Swap in the page's own copy (AHD-13726).
  const localizedValidity = {
    onInvalid: (
      e: FormEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const el = e.currentTarget
      el.setCustomValidity(
        el.validity.valueMissing ? t(k.fieldRequired) : t(k.fieldInvalidEmail)
      )
    },
    onInput: (e: FormEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      e.currentTarget.setCustomValidity(""),
  }
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [bookModalOpen, setBookModalOpen] = useState(false)

  // Open TCP/TLS sockets to Fillout immediately so the booking modal opens
  // fast when the user clicks. Cheap (no data transfer); browsers idle-close
  // the sockets if they're never used. Skipped where the call card is gated out.
  if (showBookCall) preconnectFillout()
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  })
  const [submitted, setSubmitted] = useState(false)
  const [sending, setSending] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const faqItems = initialFaqItems

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const MAX_NAME_LENGTH = 100
  const MAX_MESSAGE_LENGTH = 2000

  function validateForm(): string | null {
    if (form.firstName.length > MAX_NAME_LENGTH)
      return `First name must be ${MAX_NAME_LENGTH} characters or fewer.`
    if (form.lastName.length > MAX_NAME_LENGTH)
      return `Last name must be ${MAX_NAME_LENGTH} characters or fewer.`
    if (!EMAIL_REGEX.test(form.email))
      return "Please enter a valid email address."
    if (form.message.length > MAX_MESSAGE_LENGTH)
      return `Message must be ${MAX_MESSAGE_LENGTH} characters or fewer.`
    return null
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setValidationError(null)

    const error = validateForm()
    if (error) {
      setValidationError(error)
      return
    }

    setSending(true)
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // country + locale are the market/language the visitor is browsing in
        // (from the /{country}/{locale} URL). Sent as a structured routing
        // signal so the support drafters can ground on the right market — see
        // AHD-11075. The support side treats them as nice-to-have.
        body: JSON.stringify({ ...form, country, locale }),
      })
      if (res.ok) {
        setSubmitted(true)
      } else if (res.status === 400) {
        setValidationError(t(k.formValidationError))
      } else if (res.status === 429) {
        setValidationError(t(k.formRateLimitError))
      } else {
        setValidationError(t(k.formSendError))
      }
    } catch {
      setValidationError(t(k.formSendError))
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      {/* ─────────────── Hero ─────────────── */}
      <section className="pt-12 pb-6 md:pt-20">
        <div className="mx-auto max-w-[1440px] px-4 text-center md:px-10">
          <p className="type-label-lg text-neutral-400">{t(k.pageLabel)}</p>
          <h1 className="type-display-lg mx-auto mt-6 max-w-3xl text-balance text-neutral-900">
            {t(k.heading)}
          </h1>
          <p className="type-body-lg mx-auto mt-4 max-w-2xl text-neutral-600">
            {t(k.subtitle)}
          </p>

          {/* Team photos */}
          <div className="mt-10 flex justify-center">
            <div className="flex -space-x-2">
              {TEAM_PHOTOS.map((photo, i) => (
                <div
                  key={i}
                  className="border-background h-10 w-10 overflow-hidden rounded-full border-2"
                >
                  <Image
                    src={photo.src}
                    alt={photo.alt}
                    width={40}
                    height={40}
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Prominent phone — CH only (real staffed Swiss line) */}
          {showPhone && (
            <div className="mt-8 flex flex-col items-center justify-center gap-2">
              <a
                href={PHONE_NUMBER_HREF}
                onClick={() => track.ctaClick("phone-hero", "contact-hero")}
                className="type-headline-md text-aubergine-900 hover:text-aubergine-700 inline-flex items-center gap-2 transition-colors"
              >
                {liveNow && <LiveDot />}
                <PhoneIcon width={20} height={20} />
                {PHONE_NUMBER_DISPLAY}
              </a>
              <span className="type-label-sm text-neutral-400">
                {t(k.phoneHours)}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* ─────────────── Intent routing ─────────────── */}
      {showIntentSection && (
        <section className="py-10 md:py-14">
          <div className="mx-auto max-w-[1440px] px-4 md:px-10">
            <div>
              <h2 className="type-headline-lg text-center text-neutral-900">
                {t(k.intentHeading)}
              </h2>
            </div>

            <m.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.09 } },
              }}
              className={cn(
                "mt-10 grid grid-cols-1 items-stretch gap-4",
                intentGridCols
              )}
            >
              {showBookCall && (
                <m.div
                  variants={{
                    hidden: { opacity: 0, y: 12 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.5,
                        ease: expoOut as unknown as number[],
                      },
                    },
                  }}
                  className="h-full"
                >
                  <IntentCard
                    icon={<CalendarIcon />}
                    title={t(k.calendarTitle)}
                    description={t(k.bookCallDesc)}
                    action={t(k.calendarAction)}
                    onClick={() => setBookModalOpen(true)}
                    onWarmup={warmFillout}
                    trackLabel="book-call"
                  />
                </m.div>
              )}
              {showEmailCard && (
                <m.div
                  variants={{
                    hidden: { opacity: 0, y: 12 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.5,
                        ease: expoOut as unknown as number[],
                      },
                    },
                  }}
                  className="h-full"
                >
                  <IntentCard
                    icon={<EmailIcon />}
                    title={t(k.emailCardTitle)}
                    description={t(k.emailCardDesc)}
                    action={EMAIL_ADDRESS}
                    href={`mailto:${EMAIL_ADDRESS}`}
                    trackLabel="email-card"
                  />
                </m.div>
              )}
              <m.div
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.5,
                      ease: expoOut as unknown as number[],
                    },
                  },
                }}
                className="h-full"
              >
                <IntentCard
                  icon={<ChatBubbleIcon />}
                  title={t(k.chatTitle)}
                  description={t(k.chatDesc)}
                  action={t(k.chatNowAction)}
                  // Opens the mounted package panel (the site-wide Chatwoot
                  // launcher). A previous page-local version called the old
                  // chat widget's imperative show() behind a feature-detect
                  // guard, so once that widget stopped auto-booting this card
                  // silently did nothing (AHD-8867).
                  onClick={() => requestPanelOpen()}
                  trackLabel="live-chat"
                />
              </m.div>
              {showWhatsApp && (
                <m.div
                  variants={{
                    hidden: { opacity: 0, y: 12 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.5,
                        ease: expoOut as unknown as number[],
                      },
                    },
                  }}
                  className="h-full"
                >
                  <IntentCard
                    icon={<WhatsAppIcon />}
                    title={t(k.whatsappTitle)}
                    description={t(k.whatsappDesc)}
                    action={t(k.chatAction)}
                    href={`https://wa.me/${MARKET_SUPPORT[country].whatsappDigits}`}
                    trackLabel="whatsapp"
                  />
                </m.div>
              )}
              {showPhone && (
                <m.div
                  variants={{
                    hidden: { opacity: 0, y: 12 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.5,
                        ease: expoOut as unknown as number[],
                      },
                    },
                  }}
                  className="h-full"
                >
                  <IntentCard
                    icon={<PhoneIcon width={22} height={22} />}
                    title={t(k.callTitle)}
                    description={t(k.phoneHours)}
                    action={PHONE_NUMBER_DISPLAY}
                    href={PHONE_NUMBER_HREF}
                    trackLabel="phone"
                    liveIndicator={liveNow === true}
                  />
                </m.div>
              )}
            </m.div>

            {showBookCall && (
              <BookCallModal
                open={bookModalOpen}
                onClose={() => setBookModalOpen(false)}
                formId={getFilloutCallFormId(locale)}
              />
            )}
          </div>
        </section>
      )}

      {/* ─────────────── Email fallback form ─────────────── */}
      <section className="py-14 md:py-20">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-start gap-10 px-4 md:grid-cols-[1fr_1.2fr] md:gap-16 md:px-10">
          <div>
            {/* Where email has its own intent card (AT), the address and the
                "E-Mail senden" label would repeat it, so this column is just
                the form's heading. */}
            {!showEmailCard && (
              <div className="type-label-sm mb-4 flex items-center gap-2 font-mono tracking-[0.15em] text-neutral-500">
                <EmailIcon className="text-neutral-400" />
                {t(k.emailTitle)}
              </div>
            )}
            <h2 className="type-display-sm text-neutral-900">
              {t(k.emailSectionHeading)}
            </h2>
            <p className="type-body-lg mt-3 text-neutral-600">
              {t(k.emailSectionSubtitle)}
            </p>
            {!showEmailCard && (
              <a
                href={`mailto:${EMAIL_ADDRESS}`}
                onClick={() =>
                  track.ctaClick("email-direct", "contact-fallback")
                }
                className="type-body-md text-aubergine-800 hover:text-aubergine-600 mt-6 inline-flex items-center gap-2 underline underline-offset-4"
              >
                {EMAIL_ADDRESS}
              </a>
            )}
          </div>

          <div>
            <div className="rounded-[16px] bg-white p-6 md:p-8">
              {submitted ? (
                <div className="py-10 text-center">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                    fill="none"
                    className="mx-auto"
                    aria-hidden="true"
                  >
                    <circle cx="24" cy="24" r="22" fill="#7B8C3B" />
                    <path
                      d="M15 24l6 6 12-12"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <h3 className="type-headline-md mt-4 text-neutral-900">
                    {t(k.messageSentHeading)}
                  </h3>
                  <p className="type-body-lg mt-2 text-neutral-600">
                    {t(k.messageSentText)}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="firstName"
                        className="mb-1 block text-sm font-medium text-neutral-900"
                      >
                        {t(k.firstNameLabel)}
                      </label>
                      <input
                        id="firstName"
                        type="text"
                        required
                        {...localizedValidity}
                        maxLength={MAX_NAME_LENGTH}
                        value={form.firstName}
                        onChange={(e) =>
                          setForm({ ...form, firstName: e.target.value })
                        }
                        className="focus-visible:ring-aubergine-400 w-full rounded-lg border border-neutral-200 px-4 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus-visible:border-transparent focus-visible:ring-2 focus-visible:outline-none"
                        placeholder={t(k.firstNamePlaceholder)}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="lastName"
                        className="mb-1 block text-sm font-medium text-neutral-900"
                      >
                        {t(k.lastNameLabel)}
                      </label>
                      <input
                        id="lastName"
                        type="text"
                        required
                        {...localizedValidity}
                        maxLength={MAX_NAME_LENGTH}
                        value={form.lastName}
                        onChange={(e) =>
                          setForm({ ...form, lastName: e.target.value })
                        }
                        className="focus-visible:ring-aubergine-400 w-full rounded-lg border border-neutral-200 px-4 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus-visible:border-transparent focus-visible:ring-2 focus-visible:outline-none"
                        placeholder={t(k.lastNamePlaceholder)}
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="mb-1 block text-sm font-medium text-neutral-900"
                    >
                      {t(k.emailLabel)}
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      {...localizedValidity}
                      value={form.email}
                      onChange={(e) =>
                        setForm({ ...form, email: e.target.value })
                      }
                      className="focus-visible:ring-aubergine-400 w-full rounded-lg border border-neutral-200 px-4 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus-visible:border-transparent focus-visible:ring-2 focus-visible:outline-none"
                      placeholder={t(k.emailPlaceholder)}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="mb-1 block text-sm font-medium text-neutral-900"
                    >
                      {t(k.messageLabel)}
                    </label>
                    <textarea
                      id="message"
                      required
                      {...localizedValidity}
                      rows={5}
                      maxLength={MAX_MESSAGE_LENGTH}
                      value={form.message}
                      onChange={(e) =>
                        setForm({ ...form, message: e.target.value })
                      }
                      className="focus-visible:ring-aubergine-400 w-full resize-none rounded-lg border border-neutral-200 px-4 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus-visible:border-transparent focus-visible:ring-2 focus-visible:outline-none"
                      placeholder={t(k.messagePlaceholder)}
                    />
                  </div>
                  {validationError && (
                    <p className="text-sm text-red-600">{validationError}</p>
                  )}
                  <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="type-body-sm inline-flex items-center gap-1.5 text-neutral-500">
                      <ShieldIcon className="flex-shrink-0 text-neutral-400" />
                      {t(k.privacyNote)}
                    </p>
                    <Button
                      type="submit"
                      disabled={sending}
                      className="w-full sm:w-auto"
                    >
                      {sending ? "..." : t(k.sendButton)}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {isAllowedInCountry("section:contact.faq-cta", country) && (
        <>
          {/* ─────────────── FAQ — dark aubergine ─────────────── */}
          <section className="bg-aubergine-900 py-20">
            <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-10 px-4 md:grid-cols-[1fr_1.5fr] md:gap-16 md:px-10">
              <div>
                <h2 className="type-display-sm text-white">
                  {t(k.faqHeading)}
                </h2>
                <Link
                  href={lp("/faq")}
                  className="type-body-lg mt-6 inline-flex items-center gap-1 text-white/90 transition-all hover:gap-2 hover:text-white"
                >
                  {t(k.faqViewAll)} →
                </Link>
              </div>

              <div className="flex flex-col gap-2">
                {faqItems.map((item, index) => (
                  <div key={index}>
                    <div className="border-b border-white/15">
                      <button
                        type="button"
                        onClick={() => {
                          if (openFaq !== index)
                            track.faqExpand(item.question, "contact")
                          setOpenFaq(openFaq === index ? null : index)
                        }}
                        className="flex w-full cursor-pointer items-center justify-between py-5 text-left"
                      >
                        <span className="pr-4 text-base font-medium text-white">
                          {item.question}
                        </span>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          className={cn(
                            "flex-shrink-0 text-white/60 transition-transform",
                            openFaq === index && "rotate-45"
                          )}
                        >
                          <path
                            d="M10 4v12M4 10h12"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                      {/* safe-animate-presence: conditional-mount — gated by `openFaq === index` */}
                      <AnimatePresence>
                        {openFaq === index && (
                          <m.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                            className="overflow-hidden"
                          >
                            <div
                              className="pb-5 text-sm leading-relaxed text-white/70 [&_a]:text-white/90 [&_a]:underline [&_h5]:mt-3 [&_h5]:mb-1 [&_h5]:font-semibold [&_h5]:text-white/90 [&_li]:mb-1 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 last:[&_p]:mb-0 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
                              dangerouslySetInnerHTML={{
                                __html: sanitizeHtml(item.answer, localePrefix),
                              }}
                            />
                          </m.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ─────────────── Bottom CTA — continues dark background ─────────────── */}
          <section className="bg-aubergine-900 pb-20">
            <div className="mx-auto max-w-[1440px] px-4 text-center md:px-10">
              <div>
                <h2 className="type-display-md text-white">
                  {t(k.ctaHeading)}
                </h2>
                <div className="mt-8 flex flex-wrap justify-center gap-4">
                  <Link href={lp("/pricing")}>
                    <Button className="bg-white text-neutral-900 hover:bg-neutral-100">
                      {t(k.ctaBookButton)}
                    </Button>
                  </Link>
                  {/* Discovery-call page is gated out of NL during the waitlist phase. */}
                  {isAllowedInCountry("page:free-call", country) && (
                    <Link href={lp("/free-call")}>
                      <span className="inline-flex items-center justify-center rounded-full border border-white px-8 py-3 font-medium text-white transition-all hover:bg-white/10">
                        {t(k.ctaTalkButton)}
                      </span>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  )
}
